import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { INITIAL_FORM_DATA } from "@/components/ase";
import { buildSpanMeasurementTexts, routeArg } from "@/lib/poleMapImage";
import { fbtToNteDistance } from "@/components/ase/aseDistance";
import { ASE_OPTIONS } from "@/components/ase/ASEFormConstants";

// Shared ASE answers + job-data auto-population — used by the ASE Form and ASE Form Classic
export const useASEFormData = (jobId) => {
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [poles, setPoles] = useState([]);
  const [underground, setUnderground] = useState([]);
  const [mapConnections, setMapConnections] = useState([]);
  const [mapRoutes, setMapRoutes] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  const handleChange = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      // EF Needed (checklist) drives Site Readiness Evaluation (SVCL Output) — same position in both option lists
      if (field === "ef_needed") {
        const idx = ASE_OPTIONS.efNeeded.indexOf(value);
        if (idx >= 0) next.site_readiness_evaluation = ASE_OPTIONS.siteReadiness[idx];
      }
      return next;
    });
  };

  const regeneratePathOfApproach = (format) => {
    const texts = buildSpanMeasurementTexts({
      poles,
      underground,
      connections: mapConnections,
      routes: mapRoutes,
      spanOrder: job?.span_order || null,
      stationStartId: job?.station_start_id || null,
      drawnOnly: routeArg(job),
      spanNotes: job?.span_notes || [],
      includeStations: format === "stations",
      stationsOnly: format === "stations",
    });
    setFormData(prev => ({ ...prev, path_of_approach: texts.join(", "), path_of_approach_format: format }));
    toast.success(format === "stations" ? "Path of approach set to station numbers" : "Path of approach set to footage");
  };

  // Live: extract an email from the Building Owner Contact text into the SVCL email box
  useEffect(() => {
    const m = String(formData.building_owner_contact || "").match(/[\w.+-]+@[\w-]+\.[\w.-]+/);
    if (m && !formData.building_contact_email) {
      setFormData((prev) => (prev.building_contact_email ? prev : { ...prev, building_contact_email: m[0] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.building_owner_contact]);

  // Live: Nearest Fiber FID from the checklist fills Coordinator "Nearest Fiber FID if Missing" when empty
  useEffect(() => {
    if (formData.nearest_fiber_fid && !formData.nearest_fiber_fid_missing) {
      setFormData((prev) => (prev.nearest_fiber_fid_missing ? prev : { ...prev, nearest_fiber_fid_missing: prev.nearest_fiber_fid }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.nearest_fiber_fid]);

  const fetchJobAndASE = useCallback(async () => {
    try {
      const [jobRes, aseRes, polesRes, undergroundRes, connectionsRes, routesRes] = await Promise.all([
        axios.get(`/jobs/${jobId}`),
        axios.get(`/jobs/${jobId}/ase-data`),
        axios.get(`/jobs/${jobId}/poles`),
        axios.get(`/jobs/${jobId}/underground`),
        axios.get(`/jobs/${jobId}/connections`),
        axios.get(`/jobs/${jobId}/routes`).catch(() => ({ data: [] }))
      ]);
      setJob(jobRes.data);
      setPoles(polesRes.data || []);
      setUnderground(undergroundRes.data || []);
      
      if (aseRes.data.ase_data) {
        setFormData(prev => ({ ...prev, ...aseRes.data.ase_data }));
      }
      
      // Auto-populate from job data
      const jobData = jobRes.data;
      const polesData = polesRes.data || [];
      const ugData = undergroundRes.data || [];
      const connectionsData = connectionsRes.data || [];
      const routesLookup = {};
      (routesRes.data || []).forEach((r) => { routesLookup[`${r.from_pole_id}-${r.to_pole_id}`] = r.waypoints || []; });
      setMapConnections(connectionsData);
      setMapRoutes(routesLookup);
      
      setFormData(prev => {
        const updated = { ...prev };
        
        // Address from job
        if (!updated.address && jobData.location) {
          updated.address = jobData.location;
        }
        // Site ID + Employee Info from job
        if (!updated.site_id && jobData.site_id) {
          updated.site_id = jobData.site_id;
        }
        if (!updated.employee_info && jobData.employee_info) {
          updated.employee_info = jobData.employee_info;
        }
        // Building contact email extracted from the building owner contact text
        if (!updated.building_contact_email && updated.building_owner_contact) {
          const m = String(updated.building_owner_contact).match(/[\w.+-]+@[\w-]+\.[\w.-]+/);
          if (m) updated.building_contact_email = m[0];
        }
        // Coordinator "Nearest Fiber FID if Missing" from the checklist Nearest Fiber FID
        if (!updated.nearest_fiber_fid_missing && updated.nearest_fiber_fid) {
          updated.nearest_fiber_fid_missing = updated.nearest_fiber_fid;
        }
        
        // Starting FH/MH/POLE tie-in: the Handhole/Manhole marked Primary FID is the source of truth.
        // Overwrite the field when it is empty or still holds the last auto-filled value (manual edits are kept).
        const primaryFid = ugData.find((u) => u.primary_fid && /handhole|manhole/i.test(u.type || "") && u.tie_in_measurements);
        if (primaryFid) {
          const tieIn = primaryFid.tie_in_measurements;
          if (!updated.starting_tie_in_measurements || updated.starting_tie_in_measurements === updated.starting_tie_in_auto) {
            updated.starting_tie_in_measurements = tieIn;
          }
          updated.starting_tie_in_auto = tieIn;
        }
        
        // Pole coordinates
        if (polesData.length > 0) {
          const firstPole = polesData[0];
          const lastPole = polesData[polesData.length - 1];
          
          if (!updated.soc_pole_line_start && firstPole.latitude && firstPole.longitude) {
            updated.soc_pole_line_start = `${firstPole.latitude}, ${firstPole.longitude}`;
          }
          if (!updated.soc_pole_line_end && lastPole.latitude && lastPole.longitude) {
            updated.soc_pole_line_end = `${lastPole.latitude}, ${lastPole.longitude}`;
          }
          
          // Starting measurements from first pole
          if (!updated.starting_tie_in_measurements && firstPole) {
            const measurements = [];
            if (firstPole.span1_direction && firstPole.span1_distance) {
              measurements.push(`Span 1: ${firstPole.span1_direction} - ${firstPole.span1_distance} ft`);
            }
            if (firstPole.span2_direction && firstPole.span2_distance) {
              measurements.push(`Span 2: ${firstPole.span2_direction} - ${firstPole.span2_distance} ft`);
            }
            if (measurements.length > 0) {
              updated.starting_tie_in_measurements = measurements.join("; ");
            }
          }
          
          // Proposed pole and building height - from HOA data
          if (!updated.proposed_pole_bldg_height && polesData.length > 0) {
            const hoaData = [];
            polesData.forEach(pole => {
              const poleId = pole.pole_number || pole.pole_tag || `Pole ${polesData.indexOf(pole) + 1}`;
              // Get HOA heights from pole data
              if (pole.hoa_measurements) {
                hoaData.push(`${poleId}: ${pole.hoa_measurements}`);
              } else {
                // Try to build from individual HOA fields
                const heights = [];
                if (pole.hoa_power) heights.push(`Power: ${pole.hoa_power}`);
                if (pole.hoa_telco) heights.push(`Telco: ${pole.hoa_telco}`);
                if (pole.hoa_proposed) heights.push(`Proposed: ${pole.hoa_proposed}`);
                if (heights.length > 0) {
                  hoaData.push(`${poleId}: ${heights.join(', ')}`);
                }
              }
            });
            if (hoaData.length > 0) {
              updated.proposed_pole_bldg_height = hoaData.join('; ');
            } else {
              // Fallback: just list pole numbers
              const poleNumbers = polesData.map(p => p.pole_number || p.pole_tag).filter(Boolean).join(", ");
              if (poleNumbers) {
                updated.proposed_pole_bldg_height = `Poles: ${poleNumbers}`;
              }
            }
          }
        }
        
        // Underground items - with photo attachment
        ugData.forEach(item => {
          // Get photos from item (handle both single photo and photos array)
          const itemPhotos = item.photos || (item.photo ? [item.photo] : []);
          
          if (item.type === 'FBT' || item.type === 'FBT/NTE') {
            if (!updated.fiber_terminal_lat_long && item.latitude && item.longitude) {
              updated.fiber_terminal_lat_long = `${item.latitude}, ${item.longitude}`;
            }
            if (!updated.existing_fiber_terminal) updated.existing_fiber_terminal = "YES";
            // Attach FBT photos
            if (itemPhotos.length > 0 && (!updated.fbt_photos || updated.fbt_photos.length === 0)) {
              updated.fbt_photos = itemPhotos;
            }
          }
          if (item.type === 'NTE' || item.type === 'FBT/NTE') {
            if (!updated.nte_lat_long && item.latitude && item.longitude) {
              updated.nte_lat_long = `${item.latitude}, ${item.longitude}`;
            }
            if (!updated.existing_nte) updated.existing_nte = "YES";
            // Attach NTE photos
            if (itemPhotos.length > 0 && (!updated.nte_photos || updated.nte_photos.length === 0)) {
              updated.nte_photos = itemPhotos;
            }
          }
          if (item.type === 'SOC Start') {
            if (!updated.soc_start_lat_long && item.latitude && item.longitude) {
              updated.soc_start_lat_long = `${item.latitude}, ${item.longitude}`;
            }
            // Attach SOC Start photos
            if (itemPhotos.length > 0 && (!updated.soc_start_photos || updated.soc_start_photos.length === 0)) {
              updated.soc_start_photos = itemPhotos;
            }
          }
          if (item.type === 'SOC End') {
            if (!updated.soc_end_lat_long && item.latitude && item.longitude) {
              updated.soc_end_lat_long = `${item.latitude}, ${item.longitude}`;
            }
            // Attach SOC End photos
            if (itemPhotos.length > 0 && (!updated.soc_end_photos || updated.soc_end_photos.length === 0)) {
              updated.soc_end_photos = itemPhotos;
            }
          }
          if ((item.type === 'Easement Start' || item.type === 'Easement End') && item.latitude && item.longitude) {
            const line = `${item.type}: ${item.latitude}, ${item.longitude}`;
            const cur = updated.row_measurements || "";
            if (!cur.includes(item.type)) updated.row_measurements = cur ? `${cur}\n${line}` : line;
          }
          if (item.type === 'MPOE') {
            if (!updated.mpoe_address && item.notes) {
              updated.mpoe_address = item.notes;
            }
            // Attach MPOE photos
            if (itemPhotos.length > 0 && (!updated.mpoe_photos || updated.mpoe_photos.length === 0)) {
              updated.mpoe_photos = itemPhotos;
            }
          }
          if (item.type === 'Hoffman Box' && !updated.need_hoffman_box) {
            updated.need_hoffman_box = "YES";
          }
          if (/^(Aerial )?EMT-[SE]$/.test(item.type || '') && (!updated.need_emt || updated.need_emt === "NO")) {
            updated.need_emt = "YES";
          }
          
          // Handhole or Manhole - get tie-in measurements for "Starting FH/MH/Pole tie in measurements"
          if (item.type === 'Handhole' || item.type?.toLowerCase().includes('handhole') ||
              item.type === 'Manhole' || item.type?.toLowerCase().includes('manhole')) {
            if (item.tie_in_measurements && !updated.starting_tie_in_measurements) {
              updated.starting_tie_in_measurements = item.tie_in_measurements;
            }
          }
        });
        
        // If no handhole/manhole tie-in, check first pole for starting tie-in measurements
        if (!updated.starting_tie_in_measurements && polesData.length > 0) {
          const sortedPoles = [...polesData].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
          const firstPole = sortedPoles[0];
          // Check if first pole has any tie-in or measurement notes
          if (firstPole.notes && firstPole.notes.toLowerCase().includes('tie')) {
            updated.starting_tie_in_measurements = firstPole.notes;
          }
        }
        
        // Also collect all tie-in measurements from underground items for starting measurements
        if (!updated.starting_tie_in_measurements) {
          const allTieIns = ugData
            .filter(item => item.tie_in_measurements)
            .map(item => `${item.label || item.type}: ${item.tie_in_measurements}`)
            .join('; ');
          if (allTieIns) {
            updated.starting_tie_in_measurements = allTieIns;
          }
        }
        
        // Attach pole photos to pole line start/end
        if (polesData.length > 0) {
          const firstPole = polesData[0];
          const lastPole = polesData[polesData.length - 1];
          
          // First pole photos for pole line start
          const firstPolePhotos = firstPole.photos || (firstPole.photo ? [firstPole.photo] : []);
          if (firstPolePhotos.length > 0 && (!updated.pole_line_start_photos || updated.pole_line_start_photos.length === 0)) {
            updated.pole_line_start_photos = firstPolePhotos;
          }
          
          // Last pole photos for pole line end
          const lastPolePhotos = lastPole.photos || (lastPole.photo ? [lastPole.photo] : []);
          if (lastPolePhotos.length > 0 && (!updated.pole_line_end_photos || updated.pole_line_end_photos.length === 0)) {
            updated.pole_line_end_photos = lastPolePhotos;
          }
        }
        
        // Path of approach — mirrors the interactive map's Span Measurements list
        // (station numbers included only when the map's Sta #'s toggle is on)
        if (!updated.path_of_approach) {
          const includeStations = localStorage.getItem(`staToggle:${jobId}`) === '1';
          const texts = buildSpanMeasurementTexts({ poles: polesData, underground: ugData, connections: connectionsData, includeStations, routes: routesLookup, spanNotes: jobRes.data?.span_notes || [], spanOrder: jobRes.data?.span_order || null, drawnOnly: routeArg(jobRes.data), stationStartId: jobRes.data?.station_start_id || null });
          if (texts.length > 0) {
            updated.path_of_approach = texts.join(', ');
          }
        }
        
        // Auto-calculate SVCL Output fields
        // Site Readiness Evaluation mirrors EF Needed
        if (updated.ef_needed && !updated.site_readiness_evaluation) {
          const idx = ASE_OPTIONS.efNeeded.indexOf(updated.ef_needed);
          if (idx >= 0) updated.site_readiness_evaluation = ASE_OPTIONS.siteReadiness[idx];
        }
        // Distance from FBT to NTE / Est. Fiber Drop Length: filled live by the fbtToNteDistance effect below
        // Multi-tenant based on business units
        if (updated.number_of_business_units) {
          const units = parseInt(updated.number_of_business_units) || 0;
          updated.multi_tenant = units > 1 ? "YES" : "NO";
          updated.number_of_tenants = updated.number_of_business_units;
        }
        
        // Building demarcation
        if (updated.how_many_dmarcs) {
          const dmarcs = parseInt(updated.how_many_dmarcs) || 0;
          updated.building_demarcation = dmarcs === 1 ? "Single MPOE/Terminal" : "Multiple MPOE/Terminal";
        }
        
        // Building contact from access contact
        if (!updated.building_contact && updated.building_owner_contact) {
          updated.building_contact = updated.building_owner_contact;
        }
        
        // ========== COLLECT ALL JOB PHOTOS FOR MARK UPS GALLERY ==========
        // Only auto-populate if markup_photos is empty (don't overwrite user edits)
        if (!updated.markup_photos || updated.markup_photos.length === 0) {
          const allJobPhotos = [];
          
          // Collect photos from all poles
          polesData.forEach(pole => {
            // Main pole photo
            if (pole.photo) allJobPhotos.push(pole.photo);
            if (pole.photos && Array.isArray(pole.photos)) {
              allJobPhotos.push(...pole.photos);
            }
            // Birthmark photos
            if (pole.birthmark_photos && Array.isArray(pole.birthmark_photos)) {
              allJobPhotos.push(...pole.birthmark_photos);
            }
            // MGNV photos
            if (pole.mgnv_photos && Array.isArray(pole.mgnv_photos)) {
              allJobPhotos.push(...pole.mgnv_photos);
            }
            // Anchor photos
            if (pole.anchor_photos && Array.isArray(pole.anchor_photos)) {
              allJobPhotos.push(...pole.anchor_photos);
            }
            // HOA photos
            if (pole.hoa_photos && Array.isArray(pole.hoa_photos)) {
              allJobPhotos.push(...pole.hoa_photos);
            }
          });
          
          // Collect photos from all underground items
          ugData.forEach(item => {
            if (item.photo) allJobPhotos.push(item.photo);
            if (item.photos && Array.isArray(item.photos)) {
              allJobPhotos.push(...item.photos);
            }
          });
          
          // Remove duplicates and empty values
          const uniquePhotos = [...new Set(allJobPhotos.filter(p => p && typeof p === 'string'))];
          
          if (uniquePhotos.length > 0) {
            updated.markup_photos = uniquePhotos;
          }
        }
        
        return updated;
      });
      
    } catch (error) {
      toast.error("Failed to load job");
      navigate("/jobs");
    } finally {
      setLoading(false);
    }
  }, [jobId, navigate]);

  useEffect(() => {
    fetchJobAndASE();
  }, [fetchJobAndASE]);

  // Live map data: re-read poles/attributes/lines every few seconds and whenever this tab regains focus,
  // so edits made on the Job Map while the ASE form is open flow into the auto-filled fields
  const refreshMapData = useCallback(async () => {
    try {
      const [jobRes, polesRes, ugRes, connRes, routesRes] = await Promise.all([
        axios.get(`/jobs/${jobId}`),
        axios.get(`/jobs/${jobId}/poles`),
        axios.get(`/jobs/${jobId}/underground`),
        axios.get(`/jobs/${jobId}/connections`),
        axios.get(`/jobs/${jobId}/routes`).catch(() => ({ data: [] })),
      ]);
      const routesLookup = {};
      (routesRes.data || []).forEach((r) => { routesLookup[`${r.from_pole_id}-${r.to_pole_id}`] = r.waypoints || []; });
      const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
      setJob((prev) => (same(prev, jobRes.data) ? prev : jobRes.data));
      setPoles((prev) => (same(prev, polesRes.data || []) ? prev : polesRes.data || []));
      setUnderground((prev) => (same(prev, ugRes.data || []) ? prev : ugRes.data || []));
      setMapConnections((prev) => (same(prev, connRes.data || []) ? prev : connRes.data || []));
      setMapRoutes((prev) => (same(prev, routesLookup) ? prev : routesLookup));
    } catch (e) { /* keep last good data */ }
  }, [jobId]);

  useEffect(() => {
    if (loading) return undefined;
    const id = setInterval(refreshMapData, 8000);
    const onFocus = () => { if (document.visibilityState === "visible") refreshMapData(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => { clearInterval(id); window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onFocus); };
  }, [loading, refreshMapData]);

  // Live: Distance From FBT to NTE (Checklist) and Est. Fiber Drop Length (SVCL) share one measured value.
  // A box is updated only while it is empty or still holds the previous auto value — manual edits are kept.
  const fbtToNte = fbtToNteDistance({ formData, job, poles, underground, connections: mapConnections, routes: mapRoutes });
  useEffect(() => {
    if (loading || !fbtToNte) return;
    setFormData((prev) => {
      const auto = prev.fbt_to_nte_auto || "";
      const next = { ...prev, fbt_to_nte_auto: fbtToNte };
      if (!prev.distance_fbt_to_nte || prev.distance_fbt_to_nte === auto) next.distance_fbt_to_nte = fbtToNte;
      if (!prev.est_fiber_drop_length || prev.est_fiber_drop_length === auto) next.est_fiber_drop_length = fbtToNte;
      return next.distance_fbt_to_nte === prev.distance_fbt_to_nte && next.est_fiber_drop_length === prev.est_fiber_drop_length && auto === fbtToNte ? prev : next;
    });
  }, [loading, fbtToNte, formData.distance_fbt_to_nte, formData.est_fiber_drop_length, formData.fbt_to_nte_auto]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`/jobs/${jobId}/ase-data`, { ase_data: formData });
      toast.success("ASE data saved");
    } catch (error) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return { job, poles, underground, mapConnections, mapRoutes, loading, saving, formData, setFormData, handleChange, regeneratePathOfApproach, handleSave };
};
